import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthenticationSettingsContext } from '../../../../../../context/AuthenticationSettingsContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import {
  CreateAuthenticationSettingsResponse,
  CreateAuthenticationSettingsVariables,
  AuthenticationSettingsMutations,
  UpdateAuthenticationSettingsResponse,
  UpdateAuthenticationSettingsVariables,
} from '../../../../../../graphql/authenticationSettings.graphql';
import useMutation from '../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../providers/ToastProvider';
import {
  AuthenticationSettingsInput,
  AuthenticationSettings,
  AuthenticationSettingsUtil,
  AuthenticationType,
} from '../../../../../../types/authentication_settings';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { User } from '../../../../../../types/user';
import SaveChangesButton from '../../../../../components/Button/templates/SaveChangesButton';
import { DropdownSearchSelect } from '../../../../../components/Dropdown';
import { Surface } from '../../../../../components/Surface';
import { TextInput } from '../../../../../components/TextInput';
import { ToastType } from '../../../../../components/Toast';

interface UserUpdateAuthenticationPaneProps {
  user: User;
}

export default function UserUpdateAuthenticationPane({ user }: UserUpdateAuthenticationPaneProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { authenticationSettings, setAuthenticationSettings, authenticationSettingsForUser } =
    useContext(AuthenticationSettingsContext);

  const original = useMemo(() => {
    const forUser = authenticationSettingsForUser(user.id);
    if (forUser) return new AuthenticationSettingsInput(forUser);
  }, [authenticationSettings]);

  const [input, setInput] = useState(
    new AuthenticationSettingsInput(original || { companyId: currentCompany.id, userId: user.id }),
  );

  useEffect(() => {
    if (original) setInput(new AuthenticationSettingsInput(original));
  }, [original]);

  const handleChangesCompleted = (settings: AuthenticationSettings) => {
    setAuthenticationSettings(new Map(authenticationSettings).set(settings.id, settings));
    showToast({
      type: ToastType.success,
      title: t(TranslationKey.authenticationSettingsUpdatedSuccessfullyToastTitle).singular.upper,
    });
  };

  const [create, { loading: createLoading }] = useMutation<
    CreateAuthenticationSettingsResponse,
    CreateAuthenticationSettingsVariables
  >(AuthenticationSettingsMutations.create, {
    onCompleted: res => {
      handleChangesCompleted(res.createAuthenticationSettings);
    },
  });

  const [update, { loading: updateLoading }] = useMutation<
    UpdateAuthenticationSettingsResponse,
    UpdateAuthenticationSettingsVariables
  >(AuthenticationSettingsMutations.update, {
    onCompleted: res => {
      handleChangesCompleted(res.updateAuthenticationSettings);
    },
  });

  const handleSaveChanges = () => {
    if (input.id) {
      update({ variables: { authenticationSettings: input.forUpdate() } });
    } else {
      create({ variables: { authenticationSettings: input.forCreate() } });
    }
  };

  return (
    <div>
      <Surface>
        <div className='grid gap-4'>
          <div className='grid gap-2'>
            <p className='section-title'>{t(TranslationKey.authenticationSettingsViewTitle).singular.upper}</p>
            <p className='subtitle'>{t(TranslationKey.authenticationSettingsViewSubtitle).singular.upper}</p>
          </div>
          <div className='r-grid'>
            <DropdownSearchSelect
              label={t(TranslationKey.authenticationType).singular.upper}
              placeholder={t(TranslationKey.none).singular.upper}
              textBuilder={type => AuthenticationSettingsUtil.authenticationTypeToString(type)}
              items={[AuthenticationType.code]}
              value={input.type === AuthenticationType.none ? undefined : input.type}
              onSelect={value => setInput(input => input.withType(value || AuthenticationType.none))}
            />
            <TextInput
              label={t(TranslationKey.expiryInHours).singular.upper}
              placeholder={t(TranslationKey.expiryInHours).singular.upper}
              type={'number'}
              disabled={input.type === AuthenticationType.none}
              value={input.expiryInHours}
              onChange={value => setInput(input => input.withExpiryInHours(parseFloat(value)))}
            />
          </div>
          <SaveChangesButton
            disabled={!input.validate() || !input.hasChanges(original)}
            loading={createLoading || updateLoading}
            className='w-fit'
            onClick={handleSaveChanges}
          />
        </div>
      </Surface>
    </div>
  );
}
